'use client';

import { c as _c } from "react/compiler-runtime";
import avatarIcon from '@public/images/avatar.svg';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@src/components/Button/Button';
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink';
import { Stamp } from '@src/components/TopNavV4/Stamp';
import { useTopNav } from '@src/components/TopNavV4/TopNavContext';
import { configPublic } from '@src/config.public';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { useTrackSelectNavCta } from '@src/utils/analytics.util';
import { cn } from '@src/utils/tailwind';
import type { TopNavItem } from '../TopNavProps';
const itemClasses = 'flex items-center cursor-pointer h-full transition-all hover:text-bodi-white text-body-md! text-nowrap';
const hoverTimeouts: Record<string, number> = {};
const hoverDelay = configPublic.general.topNavDelay;
export const TopNavDesktopBar = () => {
  const $ = _c(44);
  const {
    t
  } = useLocale("common");
  const {
    user,
    getLoginHref
  } = useSession();
  let t0;
  if ($[0] !== getLoginHref) {
    t0 = getLoginHref();
    $[0] = getLoginHref;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const loginHref = t0;
  const [activeIndex, setActiveIndex] = useState(null);
  const [accountContainerWidth, setAccountContainerWidth] = useState(getInitialContainerWidth());
  const trackSelectNavCta = useTrackSelectNavCta();
  const {
    logo,
    items,
    account,
    isExpanded,
    expandedMenu,
    setExpandedMenu,
    setExpanded,
    setAccountExpanded
  } = useTopNav();
  const accountContainerRef = useRef(null);
  function getInitialContainerWidth() {
    if (!user) {
      return 302;
    }
    if (user?.hasBodiAccess) {
      return 157;
    }
    if (!user?.hasBodiAccess) {
      return 300;
    }
  }
  let t1;
  if ($[2] !== setAccountContainerWidth) {
    t1 = () => {
      const updateWidth = function updateWidth() {
        if (accountContainerRef.current) {
          setAccountContainerWidth(accountContainerRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    };
    $[2] = setAccountContainerWidth;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== activeIndex || $[6] !== expandedMenu?.title || $[7] !== isExpanded || $[8] !== setExpanded || $[9] !== setExpandedMenu || $[10] !== trackSelectNavCta) {
    t3 = items_0 => items_0?.filter(_temp).map((item_0, index) => {
      const isSelected = item_0.title === expandedMenu?.title && isExpanded;
      const componentKey = String(item_0.title);
      if (!item_0?.hasSubItems && !item_0?.hasMiniMenu) {
        return <LocalizedLink data-testid={`nav-item-${index}`} onMouseEnter={() => setExpanded(false)} onClick={() => {
          trackSelectNavCta({
            "cta url": item_0.href
          });
        }} key={componentKey} className={cn(itemClasses, "group relative")} href={item_0.href ?? ""} rel={item_0.newTab ? "noopener noreferrer" : ""} target={item_0.newTab ? "_blank" : "_self"}>{item_0.title}<Stamp text={item_0.stamp} /><div className="absolute bottom-0 left-0 h-[3px] w-full bg-bodi-accent-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100" /></LocalizedLink>;
      }
      if (item_0?.hasMiniMenu) {
        return <div key={componentKey} className={cn("group relative flex h-full items-center transition-all hover:text-bodi-white")} onMouseEnter={() => {
          setActiveIndex(index);
          setExpanded(false);
        }} onMouseLeave={() => {
          setActiveIndex(null);
        }}><LocalizedLink className={cn(itemClasses, "relative flex appearance-none items-center gap-2 font-normal")} href={item_0.href ?? ""}>{item_0.title}<Stamp text={item_0.stamp} /><div className="absolute bottom-0 left-0 z-max h-[3px] w-full bg-bodi-accent-bright-500 opacity-0 transition-all duration-200 group-hover:opacity-100" /></LocalizedLink>{item_0.hasMiniMenu && activeIndex === index && <div className="absolute top-full left-1/2 z-10 -translate-x-1/2 bg-bodi-black-text bg-linear-to-t from-white/10 to-white/0 py-5 whitespace-nowrap"><ul>{item_0.miniMenuSubItems?.map(_temp2)}</ul></div>}</div>;
      }
      return <button data-testid={`nav-item-${index}`} key={componentKey} onMouseEnter={() => {
        setExpandedMenu(item_0);
        hoverTimeouts[componentKey] = window.setTimeout(() => setExpanded(true), hoverDelay);
      }} onMouseLeave={() => {
        clearTimeout(hoverTimeouts[componentKey]);
      }} onClick={() => {
        clearTimeout(hoverTimeouts[componentKey]);
        setExpanded(!isExpanded);
      }} className={cn(itemClasses, "relative flex appearance-none items-center gap-2 font-normal", {
        "text-bodi-white font-medium": isSelected
      })}>{item_0.title}<Stamp text={item_0.stamp} /><div className={cn("absolute bottom-0 left-0 h-[3px] w-full bg-bodi-accent-bright-500 transition-opacity", {
          "opacity-100": isSelected,
          "opacity-0": !isSelected
        })} /></button>;
    });
    $[5] = activeIndex;
    $[6] = expandedMenu?.title;
    $[7] = isExpanded;
    $[8] = setExpanded;
    $[9] = setExpandedMenu;
    $[10] = trackSelectNavCta;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const renderItems = t3;
  let t4;
  if ($[12] !== accountContainerWidth || $[13] !== logo || $[14] !== setExpanded || $[15] !== trackSelectNavCta) {
    t4 = logo?.src && <div className="flex h-full items-center" style={accountContainerWidth ? {
      width: accountContainerWidth
    } : undefined}><LocalizedLink onMouseEnter={() => setExpanded(false)} onClick={() => {
        trackSelectNavCta({
          "cta url": logo?.href
        });
      }} href={logo?.href ?? ""} className="flex h-full shrink-0 items-center"><Image src={logo.src} width={logo?.desktopWidth} height={logo?.desktopHeight} alt={logo?.altText ?? ""} /></LocalizedLink></div>;
    $[12] = accountContainerWidth;
    $[13] = logo;
    $[14] = setExpanded;
    $[15] = trackSelectNavCta;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== items || $[18] !== renderItems) {
    t5 = renderItems(items);
    $[17] = items;
    $[18] = renderItems;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t5) {
    t6 = <div className="flex h-full items-center gap-6 px-4">{t5}</div>;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== setExpanded) {
    t7 = () => setExpanded(false);
    $[22] = setExpanded;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== loginHref || $[25] !== setAccountExpanded || $[26] !== t || $[27] !== trackSelectNavCta || $[28] !== user) {
    t8 = !user ? <a href={loginHref}><Button size="small" variant="black" className="border border-solid border-bodi-gray-medium" onClick={() => {
        trackSelectNavCta({
          "cta url": loginHref
        });
      }} data-testid="loginBtn">{t("login")}</Button></a> : <button onClick={() => {
      setAccountExpanded(true);
    }} data-testid="accountBtn" className="flex items-center gap-2 font-medium text-bodi-gray-butters"><Image src={avatarIcon} width={24} height={24} alt="Account icon" />{t("my-account")}</button>;
    $[24] = loginHref;
    $[25] = setAccountExpanded;
    $[26] = t;
    $[27] = trackSelectNavCta;
    $[28] = user;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== account?.bodiCta?.href || $[31] !== account?.bodiCta?.newTab || $[32] !== account?.bodiCta?.title || $[33] !== trackSelectNavCta || $[34] !== user?.hasBodiAccess) {
    t9 = !user?.hasBodiAccess && account?.bodiCta?.title && account?.bodiCta?.href && <LocalizedLink onClick={() => {
      trackSelectNavCta({
        "cta url": account?.bodiCta?.href
      });
    }} data-testid="bodiBtn" href={account?.bodiCta?.href ?? ""} rel={account?.bodiCta?.newTab ? "noopener noreferrer" : ""} target={account?.bodiCta?.newTab ? "_blank" : "_self"}><Button className="[&>div]:px-8" size="small">{account?.bodiCta?.title}</Button></LocalizedLink>;
    $[30] = account?.bodiCta?.href;
    $[31] = account?.bodiCta?.newTab;
    $[32] = account?.bodiCta?.title;
    $[33] = trackSelectNavCta;
    $[34] = user?.hasBodiAccess;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t10 = <div onMouseEnter={t7} className="flex h-full shrink-0 items-center justify-end gap-2" ref={accountContainerRef}>{t8}{t9}</div>;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t4 || $[42] !== t6) {
    t11 = <div className="relative z-50 h-12 w-full bg-bodi-black-text px-4 font-medium text-bodi-gray-butters"><div className="flex h-full items-center justify-between">{t4}{t6}{t10}</div></div>;
    $[40] = t10;
    $[41] = t4;
    $[42] = t6;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
};
function _temp(item) {
  return Boolean(item.title);
}
function _temp2(subItem, index_0) {
  return <li className="flex flex-col px-6 py-2" key={`${subItem.title}-${index_0}`}><div className="flex items-center"><Link href={subItem.href ?? ""} className="text-body-base whitespace-nowrap text-bodi-gray-butters hover:text-bodi-white hover:underline" dangerouslySetInnerHTML={{
        __html: subItem.title ?? ""
      }} /></div></li>;
}